// useVideoCommentAuthor.ts — resolves a video comment's author WITHOUT the
// network-backed useUser hook.
//
// Why: the six seed video-comment authors (core/data/creators.ts) are
// frontend-only display records — the same six the watch page already
// resolves its video *creator* from via useVideos().creators, never through
// the API. They are NOT guaranteed to exist as real backend accounts (prod
// has no demo authors — see prod-db-not-seeded). Routing comment-author
// lookups through useUser() -> GET /users/<id>/ would 404 on prod, rendering
// the raw id ("liberty-mind") with an empty handle and a dead profile link.
//
// Resolution order:
//   1. The signed-in user, when they're the comment's own author (self-authored
//      new comments) — read straight off the session, zero lookups.
//   2. The local seed-creator registry (covers every seeded video comment).
// A comment authored by neither (impossible today — new comments always use
// the signed-in user's id, and seeds only use the six creators) resolves to
// null, same as the video-creator lookup's own miss case.
import { useSession, useVideos } from '@/hooks';

export interface VideoCommentAuthor {
  id: string;
  name: string;
  handle: string;
  av: string;
  verified: boolean;
}

export function useVideoCommentAuthor(authorId: string): VideoCommentAuthor | null {
  const { currentUser } = useSession();
  const { creators } = useVideos();

  if (currentUser && currentUser.id === authorId) {
    return {
      id: currentUser.id,
      name: currentUser.name,
      handle: currentUser.handle,
      av: currentUser.avatarKey,
      verified: currentUser.verified,
    };
  }

  return creators.find((c) => c.id === authorId) ?? null;
}
